import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { LISTING_STATUS, PatchListingInput } from "@avestate/shared";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const ListingDetail = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  status: z.enum(LISTING_STATUS),
  kind: z.string(),
  contract: z.string().nullable().optional(),
  property_type: z.string().nullable().optional(),
  comune_istat: z.string().nullable().optional(),
  portals_excluded: z.array(z.string()).nullable().optional(),
  title: z.unknown().nullable(),
  city: z.string().nullable(),
  zone: z.string().nullable(),
  province: z.string().nullable(),
  region: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  published_snapshot_id: z.string().uuid().nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string(),
  cover_url: z.string().nullable().optional(),
  payload: z.record(z.unknown()).optional(),
  has_unpublished_changes: z.boolean().optional(),
});
export type ListingDetail = z.infer<typeof ListingDetail>;

// PATCH /listings/:id returns only this narrow ack (NOT the full ListingDetail),
// so parse against the real shape — otherwise safeParse throws on every save.
const PatchAck = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  status: z.enum(LISTING_STATUS),
  kind: z.string(),
  city: z.string().nullable(),
  zone: z.string().nullable(),
  updated_at: z.string(),
});

export function useListing(id: string | undefined) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["listing", id],
    enabled: !!token && !!id,
    queryFn: () => apiFetch(`/api/v1/admin/listings/${id!}`, token!, ListingDetail),
  });
}

export function usePatchListing(id: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;

  return useMutation({
    mutationFn: (input: z.infer<typeof PatchListingInput>) => {
      if (!token) throw new Error("not authenticated");
      return apiFetch(`/api/v1/admin/listings/${id}`, token, PatchAck, { method: "PATCH", body: input });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["listing", id] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
    },
  });
}

const PublishResponse = z.object({
  snapshot_id: z.string().uuid(),
  payload_hash: z.string(),
  published_at: z.string(),
  purged_url: z.string().url(),
});
const PublishError = z.object({
  error: z.object({ code: z.number(), message: z.string() }),
  missing: z.array(z.string()).optional(),
});

export function usePublishListing(id: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;

  return useMutation({
    mutationFn: async () => {
      if (!token) throw new Error("not authenticated");
      return apiFetch(`/api/v1/admin/listings/${id}/publish`, token, PublishResponse, { method: "POST" });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["listing", id] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
      void qc.invalidateQueries({ queryKey: ["snapshots", id] });
    },
  });
}

export function useDiscardChanges(id: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: () => {
      if (!token) throw new Error("not authenticated");
      return apiFetch(
        `/api/v1/admin/listings/${id}/discard-changes`,
        token,
        z.object({ ok: z.boolean(), reverted_to_version: z.number() }),
        { method: "POST" },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["listing", id] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
    },
  });
}

export { PublishError };
